import React from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from '@/components/MarketingLayout';
import { usePageSEO } from '@/utils/seo';
import {
  ORGANIZATION_SCHEMA, FOUNDER_SCHEMA, WEBSITE_SCHEMA,
  makeBreadcrumb,
} from '@/utils/schema';
import { ArrowRight } from '@phosphor-icons/react';

export default function FounderStoryPage() {
  usePageSEO({
    title: 'Why I Created PayGBot — Vickie Mcglawn’s Founder Story',
    description:
      'The story behind PayGBot — built by Vickie Mcglawn after years of running offline businesses, homeschooling twins, navigating her husband’s autoimmune disease, and learning that online business systems can create real flexibility when designed right.',
    path: '/founder-story',
    keywords:
      'PayGBot founder, Vickie Mcglawn, why PayGBot was created, online business after illness, building income with flexibility, family-friendly business systems',
    jsonLd: [
      ORGANIZATION_SCHEMA,
      FOUNDER_SCHEMA,
      WEBSITE_SCHEMA,
      makeBreadcrumb([
        { name: 'Home', path: '/' },
        { name: 'Founder Story', path: '/founder-story' },
      ]),
    ],
  });

  return (
    <MarketingLayout>
      <article data-testid="founder-story-article">
        <section className="px-4 sm:px-6 lg:px-8 pt-16 pb-8 bg-gradient-to-b from-slate-50 to-white">
          <div className="max-w-2xl mx-auto text-center space-y-4">
            <p className="text-xs uppercase tracking-widest text-blue-600 font-semibold">Founder Story</p>
            <h1 data-testid="founder-story-h1" className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 tracking-tight leading-tight">
              Why I Created PayGBot
            </h1>
            <p className="text-base text-slate-500">By Vickie Mcglawn, Founder of PayGBot</p>
          </div>
        </section>

        <section className="px-4 sm:px-6 lg:px-8 pb-16 bg-white">
          <div className="max-w-2xl mx-auto space-y-6 text-slate-700 leading-relaxed text-[17px]">
            <p>
              I have been building businesses my whole life. Bookkeeping. Accounting. Contractor compliance scheduling. Commercial roofing. Home improvement. Logging. Trucking. None of them were glamorous. All of them taught me what it actually takes to run something real — payroll on Fridays, invoices that have to clear, crews that need to be where they said they would be, paperwork the government never stops asking for.
            </p>

            <p>
              I ran those businesses the same way most small business owners do — long hours, paper trails, and a lot of personal accountability. I thought that was just how it worked. You showed up, you outworked the problem, and you kept the lights on.
            </p>

            <p>
              Then my husband got sick.
            </p>

            <p>
              It was an autoimmune disease, and the diagnosis took six months to land. Six months of doctor visits, tests, second opinions, and not knowing. During the same season, I was homeschooling our twins. I could not run my businesses the way I had been running them for years. I could not be on a roof or in a contractor’s office while my husband needed me and my kids needed me. Life had moved the goalposts, and I had to move with it.
            </p>

            <p>
              That was when I started learning online income systems.
            </p>

            <p>
              I needed something that did not require me to be in a specific place at a specific time. I learned affiliate marketing. I learned Google Ads. I started promoting products and services using social media and paid traffic. I made mistakes — expensive ones — and I made progress. And I learned something important along the way: <strong>online business systems, when they are designed correctly, can create the kind of freedom that traditional businesses almost never allow.</strong>
            </p>

            <p>
              But the deeper I went, the more frustrated I got with how the online business world packaged itself.
            </p>

            <p>
              Every "system" I bought turned out to be another course. Twelve modules. Hours of video. Worksheets. Implementation plans. By the time you finished one, you were already being sold the next one. The promise was always simple — build a real business — but the path was always the same: learn more, then learn more, then learn more.
            </p>

            <p>
              Tools were just as bad. You needed an email platform. A funnel platform. A scheduler. A graphic tool. An AI tool. A CRM. A calendar. A sales page builder. A lead magnet host. Each one wanted $30 a month. None of them talked to each other. None of them knew anything about your business after you typed it in.
            </p>

            <p>
              I kept asking myself the same question: <em>where is the platform that just runs the business with me?</em>
            </p>

            <p>
              I could not find one. So I built it.
            </p>

            <p>
              PayGBot is the system I wished I had during the season my life changed. It is an AI business operating system that helps real people — not power users, not full-time entrepreneurs, not people with eight hours a day — build and run an online business in minutes a day. It generates the assets. It plans the week. It remembers the business. It does the work with you, not at you.
            </p>

            <p>
              There are no modules. There is no homework. You produce real things the same day you sign up — a business plan, an offer, a lead magnet, a sales page, content, a daily action plan. By the end of your first week, you have a working business. By the end of your first month, you have a system that runs itself.
            </p>

            <p>
              I built PayGBot for the version of me that was sitting at the kitchen table at 11 p.m., trying to figure out how to keep things going while my husband slept and my twins finished their math worksheets. I built it for parents who only get the nap-time window. For caregivers. For side hustlers. For people who are rebuilding after something hard. For beginners who are tired of being sold the next course before they finished the last one.
            </p>

            <p>
              You do not need to be a tech person. You do not need to already have an idea. You do not need to have ever done this before. You need a platform that meets you where you are and moves your business forward in the time you actually have.
            </p>

            <p className="text-slate-900 font-medium">
              That is what PayGBot is. That is why it exists. And that is the promise I plan on keeping.
            </p>

            <p className="pt-2">— Vickie Mcglawn</p>
          </div>
        </section>

        <section className="px-4 sm:px-6 lg:px-8 py-14 bg-slate-50 border-t border-slate-200">
          <div className="max-w-2xl mx-auto text-center space-y-4">
            <h2 className="text-2xl font-semibold text-slate-900">Want to see what PayGBot actually does?</h2>
            <p className="text-slate-600">The “What Is PayGBot?” page walks through every part of the system in detail.</p>
            <div className="flex flex-wrap justify-center gap-3 pt-2">
              <Link to="/what-is-paygbot" className="inline-flex items-center space-x-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg">
                <span>See How PayGBot Works</span>
                <ArrowRight size={18} weight="bold" />
              </Link>
              <Link to="/signup" className="inline-flex items-center px-5 py-3 border border-slate-300 hover:border-slate-400 text-slate-700 font-medium rounded-lg">
                Start My Free Trial
              </Link>
            </div>
          </div>
        </section>
      </article>
    </MarketingLayout>
  );
}
